import type { ActiveRunPhase, Message, ReferenceItem } from "../../api/types.js";
import { mergeReferenceEntities } from "../sources/citationSources.js";

export const LIVE_RUN_EVENT_LIMIT = 100;

export type LiveRunBuffer = {
  runId: string;
  sessionId: string | null;
  projectId: string | null;
  phase: ActiveRunPhase;
  createdAt: string;
  messages: Message[];
  citationSources: ReferenceItem[];
  events: unknown[];
  abortController: AbortController | null;
  source: "stream" | "snapshot";
  snapshotRevision?: number;
};

type MergeInput = {
  currentSessionId: string | null;
  isWelcome: boolean;
  liveRun: LiveRunBuffer | null;
};

export function liveRunOwnsThread({ currentSessionId, isWelcome, liveRun }: MergeInput): boolean {
  if (!liveRun) return false;
  if (liveRun.sessionId) return liveRun.sessionId === currentSessionId;
  return currentSessionId === null && !isWelcome;
}

function persistedRunMessageExists(persisted: Message[], live: Message) {
  if (!live.runId) return false;
  return persisted.some((message) => message.role === live.role && message.runId === live.runId);
}

export function mergeLiveMessages(input: MergeInput & { persisted: Message[] }): Message[] {
  const { persisted, liveRun } = input;
  if (!liveRun || !liveRunOwnsThread(input)) return persisted;
  const visibleLive = liveRun.messages.filter((message) => !persistedRunMessageExists(persisted, message));
  if (!visibleLive.length) return persisted;
  return [...persisted, ...visibleLive];
}

export function mergeLiveCitationSources(input: MergeInput & { persisted: ReferenceItem[] }): ReferenceItem[] {
  const { persisted, liveRun } = input;
  if (!liveRun || !liveRunOwnsThread(input) || !liveRun.citationSources.length) return persisted;
  return mergeReferenceEntities(persisted, liveRun.citationSources);
}
